<script lang="ts">
export default {
  name: 'CarrierView',
};
</script>

<script setup lang="ts">
import { carrierService, favoriteService } from '@/services/api';
import { Carrier } from '@/types/Carrier';
import { useRoute } from 'vue-router';
import { computed, ComputedRef, onMounted, ref } from 'vue';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs';
import type { TabConfig } from '@/types/EntityTabs';
import Tabs from '@/components/v2/Tabs.vue';
import { useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const { getTabs } = useEntityTabs();

const loading = ref(true);
const carrier = ref({} as Carrier);
const favoriteProgress = ref(false);
const carrierTabsComputedRef = ref<ComputedRef<TabConfig[]> | null>(null);
const carrierTabs = computed(() => {
  const cref = carrierTabsComputedRef.value as ComputedRef<TabConfig[]> | null;
  return cref?.value ?? [];
});

async function toggleFavorite() {
  favoriteProgress.value = true;
  if (!carrier.value.favorite) {
    await favoriteService.remove('carrier', carrier.value.ID);
    carrier.value.favorite = false;
  } else {
    await favoriteService.create('carrier', carrier.value.ID);
    carrier.value.favorite = true;
  }
  favoriteProgress.value = false;
}

async function loadCarrier() {
  carrier.value = await carrierService.findById(route.params.id as string);
}

async function loadCarrierTabs() {
  try {
    carrierTabsComputedRef.value = await getTabs('carrier', route.params.id as string);
  } catch (error) {
    console.error('Error loading carrier tabs:', error);
    carrierTabsComputedRef.value = computed(() => []);
  }
}

const currentTab = computed(() => route.path.split('/')[3] || '');
const carrierTabItems = computed(() =>
  carrierTabs.value.map((t) => ({ value: t.key, label: t.label }))
);
function onCarrierTabChange(val: string) {
  const tab = carrierTabs.value.find((t) => t.key === val);
  if (tab) router.push(tab.route);
}

onMounted(async () => {
  registerResizeItem({ name: 'carrier-header', selector: 'carrier-header' });
  registerResizeItem({ name: 'carrier-tabs', selector: 'carrier-tabs' });
  await loadCarrier();
  await loadCarrierTabs();
  loading.value = false;
});
</script>

<template>
  <div>
    <PageSpinner v-if="loading" />
    <div v-else>
      <div id="carrier-header" class="inner">
        <div class="header">
          <h1>{{ carrier.name }}</h1>
          <FavoriteIndicator
            v-model="carrier.favorite"
            @update:model-value="toggleFavorite()"
            :loading="favoriteProgress" />
          <router-link
            v-if="carrier.rating"
            :to="`/carrier/${carrier.ID}/insurer-ratings`"
            data-test="rating-link">

            <div class="flex gap-1">
              <div class="label">AM BEST RATING:</div>
              <div class="value">{{ carrier.rating }}</div>
            </div>
          </router-link>
        </div>
      </div>

      <div class="w-full">
        <Tabs
          id="carrier-tabs"
          :model-value="currentTab"
          :items="carrierTabItems"
          class="uppercase w-full"
          variant="lifted"
          @update:model-value="onCarrierTabChange" />
      </div>
      <router-view></router-view>
    </div>
  </div>
</template>

<style scoped>
.header {
  display: grid;
  grid-template-columns: auto auto 1fr;
  grid-column-gap: 1rem;
  align-items: center;
  align-content: center;
  justify-content: left;
}
</style>
